import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { Space, Table, Typography } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { getFinsystemNumberFormat } from 'src/libs/utils'
import { DescriptionCell } from 'src/pages/finSystem/balanceSummary/reports/components'
import { useDividendList } from 'src/pages/finSystem/balanceSummary/reports/reportDividends/hook'
import {
  Dividend,
  DividendFilter,
} from 'src/pages/finSystem/balanceSummary/reports/reportDividends/types'
import { SortDirectionT } from 'src/pages/finSystem/balanceSummary/reports/types'
import { CryptoIcon } from 'src/pages/finSystem/wallets/walletsTable/CryptoIcon'
import styles from 'src/pages/finSystem/balanceSummary/reports/styles.module.css'

type Props = {
  sortDirection: SortDirectionT
  setSortDirection: Dispatch<SetStateAction<SortDirectionT>>
  setFilters: (values: Partial<DividendFilter>) => void
}

export const DividendListTable = ({
  sortDirection,
  setSortDirection,
  setFilters,
}: Props) => {
  const [leftOffset, setLeftOffset] = useState(0)
  const { dividendList, loading } = useDividendList()
  const amountUsdRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const { Text } = Typography

  const columns: ColumnsType<Dividend> = [
    {
      title: 'Transaction ID',
      dataIndex: 'id',
    },
    {
      title: 'Wallet',
      dataIndex: 'walletName',
      render: (name) => (
        <Space>
          <CryptoIcon name={name} size={30} /> {name}
        </Space>
      ),
    },
    {
      title: 'Date and Time',
      dataIndex: 'dateTime',
      render: (date) => <>{date}</>,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      render: (amount, record) => (
        <>
          {getFinsystemNumberFormat(amount, 2)}{' '}
          <Text type="secondary" style={{ fontSize: 16 }}>
            {record.currency}
          </Text>
        </>
      ),
    },
    {
      title: <div ref={amountUsdRef}>Amount, USD</div>,
      dataIndex: 'amountUsd',
      sorter: true,
      sortOrder: sortDirection,
      render: (amountUsd) => (
        <>
          {getFinsystemNumberFormat(amountUsd, 2)}{' '}
          <Text type="secondary" style={{ fontSize: 16 }}>
            USD
          </Text>{' '}
        </>
      ),
    },
    {
      title: 'Comment',
      dataIndex: 'comment',
      render: (comment) => (
        <DescriptionCell text={comment} style={{ fontSize: 16 }} />
      ),
    },
  ]
  const total = dividendList?.totals.totalAmountUsd
    ? String(dividendList?.totals.totalAmountUsd)
    : 0

  useEffect(() => {
    const updateOffset = () => {
      if (!amountUsdRef.current || !wrapperRef.current) return

      const headerRect = amountUsdRef.current.getBoundingClientRect()
      const wrapperRect = wrapperRef.current.getBoundingClientRect()

      setLeftOffset(headerRect.left - wrapperRect.left)
    }

    updateOffset()

    const resizeObserver = new ResizeObserver(updateOffset)

    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [dividendList])

  return (
    <div ref={wrapperRef}>
      <div className={`even-row ${styles.summery}`}>
        <div>Total Amount</div>
        <div style={{ position: 'absolute', left: leftOffset, bottom: 16 }}>
          {getFinsystemNumberFormat(total, 2)} USD
        </div>
      </div>
      <Table
        dataSource={dividendList?.items}
        loading={loading}
        columns={columns}
        onChange={(p, f, sorter) => {
          const sort = Array.isArray(sorter) ? sorter[0] : sorter
          const { order, field } = sort

          if (!order && !field) {
            setFilters({ sorterField: undefined })
          }
          if (field === 'amountUsd') {
            const sorterField = order === 'descend' ? '-amountUsd' : 'amountUsd'
            setFilters({ sorterField })
          }

          setSortDirection(order)
        }}
        size="large"
        scroll={{ x: true }}
        rowKey={(record) => record.id}
        pagination={{
          hideOnSinglePage: true,
          position: ['bottomCenter'],
        }}
        className={styles.row}
        rowClassName={(_, index) => `${index % 2 ? 'even-row' : 'odd-row'}`}
      />
    </div>
  )
}
